import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ImageUploading, { ImageListType } from 'react-images-uploading';
import { AxiosResponse } from 'axios';
import moment from 'moment';

import TextInput from 'common/text-input/text-input';
import DateSelector from 'common/date-selector/date-selector';
import PlainNavbar from 'components/plainNavbar/plainNavbar';
import './add-new-diary.scss';
import { ReactComponent as AddUploadIcon } from 'assets/icons/add_upload.svg';
import workADiaryDummyData from 'dummy-data/work-a-diary.json';
import useLoadingStore from 'zustands/useLoadingStore';
import useApi from 'hooks/useApi';
import useErrorLog from 'hooks/useErrorLog';
import useAlertConfirmStore from 'zustands/useAlertConfirmStore';
import { INoticeItem } from 'types/notice';
import useUserStore from 'zustands/useUserStore';

interface INoticeForm {
  title: string;
  content: string;
}

const AddNewDiary: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const noticeItem: INoticeItem = location.state?.noticeItem;
  const selectedDate: string | undefined = location.state?.selectedDate;

  const { setLoadingState } = useLoadingStore();
  const { setModalState } = useAlertConfirmStore();
  const { userName } = useUserStore();

  const { webApiNode } = useApi();
  const { checkErrorLog } = useErrorLog();

  const imageList = noticeItem?.IMG_LIST?.split('@').map((i) => ({ dataURL: i })) ?? [];
  const [uploadedFiles, setUploadedFiles] = React.useState<ImageListType>(imageList);
  const [noticeForm, setNoticeForm] = React.useState<INoticeForm>({
    title: noticeItem?.TITLE ?? '',
    content: noticeItem?.CONTENTS ?? '',
  });
  // 작성일자: 수정 시 기존 ADDDATE, 추가 시 목록에서 선택한 날짜(없으면 오늘)
  const [date, setDate] = React.useState<Date>(
    noticeItem?.ADDDATE
      ? moment(noticeItem.ADDDATE).toDate()
      : selectedDate
      ? moment(selectedDate).toDate()
      : new Date(),
  );

  // 날짜 선택 핸들러
  const handleDateChange = (value: Date) => {
    setDate(value);
  };

  // 파일 업로드 시 상태 저장
  const handleFileInput = (value: ImageListType) => {
    setUploadedFiles(value);
  };
  // 폼 입력 시 상태 저장 핸들
  const handleForm = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNoticeForm({
      ...noticeForm,
      [name]: value,
    });
  };

  // 저장하기
  const submitForm = async () => {
    if (noticeForm.title === '') {
      return setModalState({ isModalShow: true, title: '지시사항 등록', desc: '타이틀을 입력해 주세요' });
    }
    if (noticeForm.content === '') {
      return setModalState({ isModalShow: true, title: '지시사항 등록', desc: '내용을 입력해 주세요' });
    }

    const submitType = noticeItem ? 'mod' : 'add';
    if (uploadedFiles.length > 0) {
      const formData = new FormData();

      let oldImgList = '';
      uploadedFiles.map((i) => {
        if (i.file) {
          formData.append('image', i.file);
        } else {
          oldImgList = oldImgList + i.dataURL + '@';
        }
      });
      if (submitType === 'add') {
        formData.append('TITLE', noticeForm.title);
        formData.append('CONTENTS', noticeForm.content);
        formData.append('ADDDATE', moment(date).format('YYYY-MM-DD'));
        formData.append('STORE', '1');
        formData.append('EMP_NAME', userName ?? '');
      } else {
        formData.append('TITLE', noticeForm.title);
        formData.append('CONTENTS', noticeForm.content);
        formData.append('ADDDATE', moment(date).format('YYYY-MM-DD'));
        formData.append('NOTICE_SEQ', noticeItem.NOTICE_SEQ.toString());
        formData.append('CLOSE_FLAG', '0');

        if (oldImgList.length > 0) {
          formData.append('OLDLIST', oldImgList.slice(0, -1));
        }
      }

      const url = submitType === 'add' ? `/auth/setNoticeImg` : `/auth/updateNoticeImg`;
      try {
        setLoadingState({ isFetchLoading: true });
        const { data }: AxiosResponse['data'] = await webApiNode('post', url, formData);

        if (data.resultCode === 0) {
          navigate('/work-a-diary', { state: { listDate: moment(date).format('YYYY-MM-DD') } });
          setModalState({
            isModalShow: true,
            title: `업무일지 ${submitType === 'add' ? `추가` : `수정`}`,
            desc: '완료되었습니다',
          });
        } else {
          throw new Error(data.resultMsg);
        }
      } catch (err) {
        checkErrorLog(err, ' /auth/setNoticeImg');
      } finally {
        setLoadingState({ isFetchLoading: false });
      }
    } else {
      const url = submitType === 'add' ? `/auth/setNotice` : `/auth/updateNotice`;
      const obj =
        submitType === 'add'
          ? {
              TITLE: noticeForm.title,
              CONTENTS: noticeForm.content,
              STORE: '1',
              EMP_NAME: userName,
              ADDDATE: moment(date).format('YYYY-MM-DD'),
            }
          : {
              CLOSE_FLAG: '0',
              NOTICE_SEQ: noticeItem.NOTICE_SEQ,
              TITLE: noticeForm.title,
              CONTENTS: noticeForm.content,
              ADDDATE: moment(date).format('YYYY-MM-DD'),
            };
      try {
        setLoadingState({ isFetchLoading: true });
        const { data }: AxiosResponse['data'] = await webApiNode('post', url, obj);

        if (data.resultCode === 0) {
          navigate('/work-a-diary', { state: { listDate: moment(date).format('YYYY-MM-DD') } });
          setModalState({
            isModalShow: true,
            title: `업무일지 ${submitType === 'add' ? '추가' : '수정'}`,
            desc: '완료되었습니다',
          });
        } else {
          throw new Error(data.resultMsg);
        }
      } catch (err) {
        checkErrorLog(err, ` ${url}`);
      } finally {
        setLoadingState({ isFetchLoading: false });
      }
    }
  };

  return (
    <>
      <PlainNavbar save={submitForm} backgroundColor="#f3f4f6" route="work-a-diary">
        <div className="add-new-diary">
          <div className="add-new-diary__head">
            <div className="add-new-diary__head__text">업무일지 {noticeItem ? '수정' : '추가'}</div>
            <div className="add-new-diary__head__subtext">지시사항 정보를 입력해주세요</div>
          </div>
          <div className="add-new-diary__card">
            <div className="add-new-diary__card__left">
              <div className="add-new-diary__basic-info">
                <div className="add-new-diary__basic-info__head">기본 정보</div>
                <div className="add-new-diary__basic-info__title">
                  <TextInput
                    inputData={handleForm}
                    placeholder="타이틀 입력"
                    label="타이틀"
                    inputName="title"
                    value={noticeForm.title}
                  />
                </div>
                <div className="add-new-diary__basic-info__date">
                  <DateSelector label="날짜" onChange={handleDateChange} value={date} disabled={!!noticeItem} />
                  {noticeItem && (
                    <div className="add-new-diary__basic-info__date__guide">
                      수정 시에는 날짜를 변경할 수 없습니다.
                    </div>
                  )}
                </div>
                <div className="add-new-diary__basic-info__content">
                  <div className="add-new-diary__basic-info__content__head">내용</div>
                  <textarea
                    name="content"
                    className="add-new-diary__basic-info__content__text-area"
                    placeholder="내용 입력"
                    value={noticeForm.content}
                    onChange={handleForm}></textarea>
                </div>
                <div className="add-new-diary__basic-info__image-upload">
                  <div className="add-new-diary__basic-info__head">사진</div>
                  <div className="add-new-diary__file-upload">
                    <ImageUploading multiple value={uploadedFiles} onChange={handleFileInput} dataURLKey="dataURL">
                      {({
                        imageList,
                        onImageUpload,
                        onImageRemoveAll,
                        onImageUpdate,
                        onImageRemove,
                        isDragging,
                        dragProps,
                      }) => (
                        <>
                          {imageList.map((image, index) => {
                            return (
                              <button
                                className="add-new-diary__file-upload__preview"
                                onClick={() => {
                                  onImageRemove(index);
                                }}>
                                <img src={image['dataURL']} alt={`${image.dataURL}`} width="100" height="100" />
                              </button>
                            );
                          })}
                          <div>
                            <label
                              className="add-new-diary__file-upload__uploader flex flex-col items-center"
                              onClick={onImageUpload}>
                              <div>
                                <AddUploadIcon />
                              </div>
                              <div>사진 추가</div>
                            </label>
                          </div>
                        </>
                      )}
                    </ImageUploading>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PlainNavbar>
    </>
  );
};
export default AddNewDiary;
